import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Users, BookOpen, CalendarCheck, Clock, AlertTriangle, ArrowRight, CreditCard, Calendar as CalendarIcon } from "lucide-react";

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-white rounded-xl p-6 border border-[hsl(var(--brand-border))] card-lift" data-testid={testid}>
      <div className="flex items-center gap-3 text-[hsl(var(--brand-muted))]">
        <Icon className="w-4 h-4" />
        <div className="text-xs tracking-[0.2em] uppercase">{label}</div>
      </div>
      <div className="font-display text-5xl mt-3 text-[hsl(var(--brand-ink))]">{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState({ expiring_tomorrow: [], expired: [], payment_expiring: [], payment_expired: [], upcoming_followups: [], todays_events: [] });
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, r, c] = await Promise.all([
        api.get("/admin/dashboard"),
        api.get("/admin/reminders"),
        api.get("/admin/clients"),
      ]);
      setStats(s.data);
      setReminders(r.data);
      setRecent(c.data.slice(0, 5));
    })();
  }, []);

  const hasAnyReminder = (reminders.expiring_tomorrow.length + reminders.expired.length + reminders.payment_expiring.length + reminders.payment_expired.length + reminders.upcoming_followups.length + reminders.todays_events.length) > 0;

  return (
    <div data-testid="admin-dashboard">
      <div className="text-xs tracking-[0.3em] uppercase text-[hsl(var(--brand-terracotta))]">Dashboard</div>
      <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">Good to see you.</h1>
      <p className="text-[hsl(var(--brand-muted))] mt-2 max-w-xl">A quick read of your practice today.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
        <Stat icon={Users} label="Clients" value={stats?.clients ?? "—"} testid="stat-clients" />
        <Stat icon={CalendarCheck} label="Active plans" value={stats?.active_plans ?? "—"} testid="stat-active-plans" />
        <Stat icon={Clock} label="Total plans" value={stats?.total_plans ?? "—"} testid="stat-total-plans" />
        <Stat icon={BookOpen} label="Recipes" value={stats?.recipes ?? "—"} testid="stat-recipes" />
      </div>

      <section className="mt-14">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-[hsl(var(--brand-terracotta))]">Reminders</div>
            <h2 className="font-display text-2xl md:text-3xl mt-1">Plans, payments, follow-ups</h2>
          </div>
          <Link to="/admin/calendar" className="text-sm text-[hsl(var(--brand-sage))] flex items-center gap-1">
            Open calendar <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {!hasAnyReminder ? (
          <div className="mt-4 bg-white rounded-xl border border-[hsl(var(--brand-border))] p-10 text-center font-serif-italic text-[hsl(var(--brand-muted))]">
            You're all caught up.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6 mt-6">
            {reminders.todays_events.length ? (
              <div className="rounded-xl border border-[hsl(var(--brand-sage))]/40 bg-white p-6" data-testid="reminders-today-events">
                <div className="flex items-center gap-2 text-[hsl(var(--brand-sage))]">
                  <CalendarIcon className="w-4 h-4" />
                  <div className="text-xs tracking-[0.2em] uppercase">Today</div>
                </div>
                <ul className="mt-4 space-y-3">
                  {reminders.todays_events.map((e) => (
                    <li key={e.id} className="flex items-center justify-between bg-[hsl(var(--brand-cream-deep))] rounded-lg p-3 border border-[hsl(var(--brand-border))]">
                      <div>
                        <div className="font-medium">{e.title}</div>
                        <div className="text-xs text-[hsl(var(--brand-muted))]">{e.time || "—"}{e.client ? ` · ${e.client.name}` : ""}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {reminders.upcoming_followups.length ? (
              <div className="rounded-xl border border-[hsl(var(--brand-border))] bg-white p-6" data-testid="reminders-followups">
                <div className="flex items-center gap-2 text-[hsl(var(--brand-sage))]">
                  <CalendarIcon className="w-4 h-4" />
                  <div className="text-xs tracking-[0.2em] uppercase">Upcoming follow-ups (7 days)</div>
                </div>
                <ul className="mt-4 space-y-3">
                  {reminders.upcoming_followups.map((e) => (
                    <li key={e.id} className="flex items-center justify-between bg-[hsl(var(--brand-cream))] rounded-lg p-3 border border-[hsl(var(--brand-border))]">
                      <div>
                        <div className="font-medium">{e.client?.name || e.title}</div>
                        <div className="text-xs text-[hsl(var(--brand-muted))]">{e.date}{e.time ? ` · ${e.time}` : ""}</div>
                      </div>
                      {e.client?.id ? (
                        <Link to={`/admin/clients/${e.client.id}`} className="text-xs text-[hsl(var(--brand-sage))]">Open</Link>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {reminders.payment_expiring.length ? (
              <div className="rounded-xl border border-[hsl(var(--brand-terracotta))]/40 bg-[hsl(var(--brand-terracotta))]/5 p-6" data-testid="reminders-payment-expiring">
                <div className="flex items-center gap-2 text-[hsl(var(--brand-terracotta))]">
                  <CreditCard className="w-4 h-4" />
                  <div className="text-xs tracking-[0.2em] uppercase">Payments ending in 7 days</div>
                </div>
                <ul className="mt-4 space-y-3">
                  {reminders.payment_expiring.map((c) => (
                    <li key={c.id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-[hsl(var(--brand-border))]">
                      <div>
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-[hsl(var(--brand-muted))]">{c.phone} · paid until {c.paid_until}</div>
                      </div>
                      <Link to={`/admin/clients/${c.id}`} className="text-xs text-[hsl(var(--brand-sage))]">Open</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {reminders.payment_expired.length ? (
              <div className="rounded-xl border border-[hsl(var(--destructive))]/30 bg-red-50/50 p-6" data-testid="reminders-payment-expired">
                <div className="flex items-center gap-2 text-[hsl(var(--destructive))]">
                  <CreditCard className="w-4 h-4" />
                  <div className="text-xs tracking-[0.2em] uppercase">Payment expired</div>
                </div>
                <ul className="mt-4 space-y-3">
                  {reminders.payment_expired.map((c) => (
                    <li key={c.id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-[hsl(var(--brand-border))]">
                      <div>
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-[hsl(var(--brand-muted))]">Expired {c.paid_until}</div>
                      </div>
                      <Link to={`/admin/clients/${c.id}`} className="text-xs text-[hsl(var(--brand-sage))]">Open</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {reminders.expiring_tomorrow.length ? (
              <div className="rounded-xl border border-[hsl(var(--brand-border))] bg-[hsl(var(--brand-cream-deep))] p-6" data-testid="reminders-expiring">
                <div className="flex items-center gap-2 text-[hsl(var(--brand-sage))]">
                  <AlertTriangle className="w-4 h-4" />
                  <div className="text-xs tracking-[0.2em] uppercase">Plan ending tomorrow</div>
                </div>
                <ul className="mt-4 space-y-3">
                  {reminders.expiring_tomorrow.map((p) => (
                    <li key={p.id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-[hsl(var(--brand-border))]">
                      <div>
                        <div className="font-medium">{p.client?.name}</div>
                        <div className="text-xs text-[hsl(var(--brand-muted))]">{p.title} · ends {p.end_date}</div>
                      </div>
                      <Link to={`/admin/clients/${p.client?.id}`} className="text-sm text-[hsl(var(--brand-sage))] flex items-center gap-1">
                        Open <ArrowRight className="w-3 h-3" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {reminders.expired.length ? (
              <div className="rounded-xl border border-[hsl(var(--brand-border))] bg-white p-6" data-testid="reminders-expired">
                <div className="flex items-center gap-2 text-[hsl(var(--brand-terracotta))]">
                  <Clock className="w-4 h-4" />
                  <div className="text-xs tracking-[0.2em] uppercase">Expired plans · no active</div>
                </div>
                <ul className="mt-4 space-y-3">
                  {reminders.expired.map((p) => (
                    <li key={p.id} className="flex items-center justify-between bg-[hsl(var(--brand-cream))] rounded-lg p-3 border border-[hsl(var(--brand-border))]">
                      <div>
                        <div className="font-medium">{p.client?.name}</div>
                        <div className="text-xs text-[hsl(var(--brand-muted))]">Ended {p.end_date}</div>
                      </div>
                      <Link to={`/admin/clients/${p.client?.id}`} className="text-sm text-[hsl(var(--brand-sage))] flex items-center gap-1">
                        Plan next <ArrowRight className="w-3 h-3" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        )}
      </section>

      <section className="mt-14">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl md:text-3xl">Recent clients</h2>
          <Link to="/admin/clients" className="text-sm text-[hsl(var(--brand-sage))] flex items-center gap-1" data-testid="view-all-clients-link">
            View all <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="mt-4 rounded-xl border border-[hsl(var(--brand-border))] bg-white overflow-hidden">
          {recent.length === 0 ? (
            <div className="p-8 font-serif-italic text-[hsl(var(--brand-muted))]">Add your first client to get started.</div>
          ) : recent.map((c) => (
            <Link
              key={c.id}
              to={`/admin/clients/${c.id}`}
              className="block px-5 py-4 border-b last:border-b-0 border-[hsl(var(--brand-border))] hover:bg-[hsl(var(--brand-cream))]"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-[hsl(var(--brand-ink))]">{c.name}</div>
                  <div className="text-xs text-[hsl(var(--brand-muted))] mt-0.5">
                    {c.phone || "no phone"} · {c.active_plan ? "Active plan" : "No active plan"}
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-[hsl(var(--brand-muted))]" />
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
